import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Configurer axios pour inclure automatiquement le token
  useEffect(() => {
    const token = localStorage.getItem('6w2h_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('🔐 Token configuré:', token.substring(0, 20) + '...');
    } else {
      console.log('❌ Aucun token trouvé');
    }
  }, []);

  // Vérifier le token au chargement de l'app
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('6w2h_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get('/api/auth/verify');
      if (response.data.success && response.data.valid) {
        setUser(response.data.user);
      } else {
        localStorage.removeItem('6w2h_token');
        delete axios.defaults.headers.common['Authorization'];
      }
    } catch (error) {
      console.error('Erreur vérification auth:', error);
      localStorage.removeItem('6w2h_token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);

      const response = await axios.post('/api/auth/login', {
        email: email.toLowerCase().trim(),
        password
      });

      if (response.data.success) {
        const { token, user } = response.data;
        
        // Stocker le token
        localStorage.setItem('6w2h_token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        console.log('✅ Connexion réussie, token stocké');
        
        // Mettre à jour l'état
        setUser(user);
        
        return { success: true, user };
      } else {
        throw new Error(response.data.error || 'Erreur de connexion');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erreur de connexion';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Nettoyer l'état local dans tous les cas
      localStorage.removeItem('6w2h_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setError(null);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setError(null);
      const response = await axios.put('/api/auth/profile', profileData);
      
      if (response.data.success) {
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      } else {
        throw new Error(response.data.error || 'Erreur de mise à jour');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erreur de mise à jour';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      const response = await axios.put('/api/auth/change-password', {
        currentPassword,
        newPassword
      });
      
      if (response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        throw new Error(response.data.error || 'Erreur changement mot de passe');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erreur changement mot de passe';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;

    const permissions = {
      operateur: ['read_own', 'create_form', 'update_own'],
      superviseur: ['read_all', 'create_form', 'update_all', 'approve_form', 'view_dashboard'],
      manager: ['read_all', 'create_form', 'update_all', 'approve_form', 'delete_form', 'manage_users', 'view_dashboard', 'admin_access']
    };

    return permissions[user.role]?.includes(permission) || false;
  };

  const isAuthenticated = () => {
    return !!user && !!localStorage.getItem('6w2h_token');
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    updateProfile,
    changePassword,
    hasPermission,
    isAuthenticated,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
